import { useMemo, useRef, useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsMore from "highcharts/highcharts-more";
import HighchartsReact from "highcharts-react-official";
import { Empty, Typography } from "antd";
import { useSlicerVariables, useWidgetData } from "../Contexts/dashboardHooks";
import { useChartSync } from "../Contexts/ChartSyncContext";
import { WidgetInfoButton } from "../Info/WidgetInfoButton";
import { resolveTemplate } from "../Utils/resolveTemplate";
import "@styles/styles-GaugeWidget.css";

HighchartsMore(Highcharts);

const { Text } = Typography;

const CONTAINER_STYLE: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  width: "100%",
};

export function GaugeWidget({ id }: { id: string }) {
  const data = useWidgetData(id);
  const chartRef = useRef<HighchartsReact.RefObject | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const syncContext = useChartSync();
  const requestSyncRef = useRef<() => void>(() => {});
  if (syncContext) requestSyncRef.current = syncContext.requestSync;

  const slicerValues = useSlicerVariables();
  const rawTitle = data?.type === "gauge" ? data.spec.display.title : null;
  const chartTitle = rawTitle ? resolveTemplate(rawTitle, slicerValues).resolved : null;

  const options = useMemo((): Highcharts.Options => {
    if (!data || data.type !== "gauge") return {} as Highcharts.Options;
    const base = data.gauge.chartOptions;
    return {
      ...base,
      chart: {
        ...((base.chart as Record<string, unknown>) ?? {}),
        reflow: true,
        events: {
          load() {
            requestSyncRef.current();
          },
        },
      },
    };
  }, [data]);

  useEffect(() => {
    if (!syncContext) return;
    const getCell = () =>
      containerRef.current?.closest(".grid-item-wrapper") ?? containerRef.current ?? null;
    const getChart = () => chartRef.current?.chart ?? null;

    syncContext.register(id, getCell, getChart);
    const t = setTimeout(() => syncContext.requestSync(), 200);

    return () => {
      clearTimeout(t);
      syncContext.unregister(id);
    };
  }, [id, syncContext]);

  const hasData = data?.type === "gauge" && data.gauge.needleValue != null;

  if (!data || data.type !== "gauge") {
    return (
      <div className="gauge-widget" data-grid-id={id} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No gauge data" />
      </div>
    );
  }

  return (
    <div ref={containerRef} className="gauge-widget" data-grid-id={id}>
      <div className="widget-header">
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          {chartTitle && <Text strong style={{ fontSize: 18 }}>{chartTitle}</Text>}
          <WidgetInfoButton id={id} />
        </span>
      </div>
      <div className="gauge-widget-body" style={{ position: "relative" }}>
        <HighchartsReact
          highcharts={Highcharts}
          options={options}
          ref={chartRef}
          containerProps={{ style: CONTAINER_STYLE }}
        />
        {!hasData && (
          <div style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              No data found in the specified date range
            </Text>
          </div>
        )}
      </div>
    </div>
  );
}
